import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

// Shared QC inspection dialog (used by Production Queue drawer and QC worklist).
export default function DpQcDialog({ job, reasons, onClose, onDone }) {
  const batch = Number(job.active_batch_qty || 0);
  const fromRework = !!job.current_rework_id;
  const available = Number((fromRework ? job.rework_open_qty : job.pending_qty) ?? batch);
  const [f, setF] = useState({ passed_qty: batch, rejected_qty: 0, disposition: "REWORK", qc_reason: "", target_step: (job.route_snapshot || [])[0] || "PRINTING", notes: "" });
  const [saving, setSaving] = useState(false);
  const rejected = Number(f.rejected_qty || 0);
  const submit = async () => {
    if (saving) return;
    const body = {
      inspected_qty: batch, passed_qty: Number(f.passed_qty || 0), rejected_qty: rejected,
      disposition: rejected > 0 ? f.disposition : null,
      qc_reason: rejected > 0 ? f.qc_reason : null,
      target_step: rejected > 0 && f.disposition === "REWORK" ? f.target_step : null,
      notes: f.notes,
    };
    if (Number(f.passed_qty) + rejected !== batch) return toast.error(`Passed + Rejected harus = ${batch} (seluruh batch)`);
    if (Number(f.passed_qty) + rejected > available) return toast.error(`Qty QC melebihi sisa menunggu QC (${available} ${job.unit || ""})`);
    if (rejected > 0 && !f.qc_reason) return toast.error("Alasan QC wajib saat ada reject");
    setSaving(true);
    try { const r = await api.post(`/digital-printing/jobs/${job.id}/qc`, body); toast.success(`QC ${r.data.qc_result}`); onDone(); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };
  const Bucket = ({ label, value, cls, tid }) => (
    <div className="rounded border border-slate-200 bg-slate-50 px-2 py-1.5 text-center" data-testid={tid}>
      <div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`text-sm font-semibold ${cls || "text-slate-700"}`}>{value}</div>
    </div>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dp-qc-dialog">
        <DialogHeader><DialogTitle>Inspeksi QC — {job.job_number}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-5 gap-1.5">
          <Bucket label="Qty Masuk QC" value={`${available}`} cls="text-indigo-600" tid="dp-qc-bk-input" />
          <Bucket label="Diterima" value={`${Number(job.accepted_qty || 0)}`} cls="text-emerald-600" tid="dp-qc-bk-accepted" />
          <Bucket label="Menunggu" value={`${Number(job.pending_qty || 0)}`} tid="dp-qc-bk-pending" />
          <Bucket label="Rework" value={`${Number(job.rework_open_qty || 0)}`} cls="text-amber-600" tid="dp-qc-bk-rework" />
          <Bucket label="Final Reject" value={`${Number(job.final_reject_qty || 0)}`} cls="text-rose-600" tid="dp-qc-bk-reject" />
        </div>
        <p className="text-xs text-slate-500">Batch cycle ini: <b>{batch} {job.unit}</b>. Passed + Rejected harus sama dengan batch.</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs">Passed Qty<Input type="number" min="0" value={f.passed_qty} onChange={(e) => setF({ ...f, passed_qty: e.target.value })} data-testid="dp-qc-passed" /></label>
          <label className="text-xs">Rejected Qty<Input type="number" min="0" value={f.rejected_qty} onChange={(e) => setF({ ...f, rejected_qty: e.target.value })} data-testid="dp-qc-rejected" /></label>
          {rejected > 0 && (
            <>
              <label className="text-xs">Disposition<select value={f.disposition} onChange={(e) => setF({ ...f, disposition: e.target.value })} className="mt-1 block w-full rounded border px-2 py-2 text-sm" data-testid="dp-qc-disposition"><option value="REWORK">REWORK</option><option value="FINAL_REJECT">FINAL_REJECT</option></select></label>
              <label className="text-xs">Alasan QC<select value={f.qc_reason} onChange={(e) => setF({ ...f, qc_reason: e.target.value })} className="mt-1 block w-full rounded border px-2 py-2 text-sm" data-testid="dp-qc-reason"><option value="">- pilih -</option>{(reasons || []).map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}</select></label>
              {f.disposition === "REWORK" && <label className="col-span-2 text-xs">Target Step Rework<select value={f.target_step} onChange={(e) => setF({ ...f, target_step: e.target.value })} className="mt-1 block w-full rounded border px-2 py-2 text-sm" data-testid="dp-qc-target">{(job.route_snapshot || []).map((s) => <option key={s} value={s}>{s}</option>)}</select></label>}
            </>
          )}
          <label className="col-span-2 text-xs">Catatan<Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Batal</Button>
          <Button onClick={submit} disabled={saving} data-testid="dp-qc-submit" className="bg-emerald-600 hover:bg-emerald-500">{saving ? "Menyimpan..." : "Simpan QC"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
